// useSessionSync.ts — keeps the Sessions page's lists in sync with the
// manager and the harnesses over sockets.
//
// The page holds one connection to the manager — the session list, and
// each row's live pod phase — and one to every ready session's harness,
// for what is happening inside that conversation. The split follows
// ownership: the manager owns the rows and the Pods, so it is the only
// thing that can say a session is still provisioning, while a harness
// owns its own turns. See live/sessionLive.ts.
//
// Also owns the initial REST load: the manager socket opens with the same
// list, but a browser or proxy that refuses the upgrade would otherwise
// leave the page permanently empty rather than merely un-live.
import React, { useCallback, useEffect, useMemo } from 'react';
import { apiClient, sessionsApi, workspacesApi } from '../api';
import type { Session, Workspace } from '../api';
import { evictAll } from './live/channelStore';
import { connectManager, disconnectAll, syncHarnesses } from './live/sessionLive';
import { insertIfAbsent, readyKey, removeById, upsertById } from './sessionRows';

export interface SessionSyncArgs {
  sessions: Session[];
  /** Each row's pod phase, from the manager socket. */
  phases: Record<string, string | undefined>;
  /** The session whose harness socket carries full transcript frames. */
  focusedId: string;
  setSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  setWorkspaces: React.Dispatch<React.SetStateAction<Workspace[]>>;
  setSelectedId: React.Dispatch<React.SetStateAction<string | null>>;
  setCodeWorkspaceId: React.Dispatch<React.SetStateAction<string | null>>;
}

/** Returns `refreshSessions`, the REST reload of sessions and workspaces. */
export function useSessionSync({
  sessions, phases, focusedId,
  setSessions, setWorkspaces, setSelectedId, setCodeWorkspaceId,
}: SessionSyncArgs): () => Promise<void> {
  const refreshSessions = useCallback(async () => {
    try {
      const [list, spaces] = await Promise.all([sessionsApi.list(), workspacesApi.list()]);
      setSessions(list);
      setWorkspaces(spaces);
    } catch (err) {
      console.error('Failed to list sessions:', err);
    }
  }, [setSessions, setWorkspaces]);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  // The manager socket: the list, live. Every handler is a functional
  // update, which is what lets this connect once and never reconnect on a
  // re-render. A socket that reconnects after a drop replaces the list
  // outright instead of layering events onto a base that missed some.
  useEffect(() => {
    connectManager<Session, Workspace>('/api/ws', apiClient.getToken() ?? '', {
      onSessions: (list) => setSessions(list ?? []),
      onCreated: (s) => setSessions((prev) => insertIfAbsent(prev, s)),
      onUpdated: (s) => setSessions((prev) => upsertById(prev, s)),
      onDeleted: (id) => {
        console.debug('[sessions] session deleted remotely:', id);
        setSessions((prev) => removeById(prev, id));
        // A session deleted from another tab must not leave this one
        // reading a transcript that no longer exists.
        setSelectedId((cur) => (cur === id ? null : cur));
      },
      onWorkspaces: (list) => setWorkspaces(list ?? []),
      onWorkspaceUpserted: (ws) => setWorkspaces((prev) => upsertById(prev, ws)),
      // Its sessions arrive as session_deleted frames of their own.
      onWorkspaceDeleted: (id) => {
        console.debug('[sessions] workspace deleted remotely:', id);
        setWorkspaces((prev) => removeById(prev, id));
        setCodeWorkspaceId((cur) => (cur === id ? null : cur));
      },
    });
    return () => { disconnectAll(); };
    // Connect once: every handler above only uses stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One socket per ready session, reconciled whenever the list or the
  // selection moves. A suspended or provisioning session has no Pod to
  // reach; it connects on the phase frame that says it is up.
  const ready = useMemo(() => readyKey(sessions, phases), [sessions, phases]);
  useEffect(() => {
    syncHarnesses(ready ? ready.split(',') : [], focusedId, apiClient.getToken() ?? '');
  }, [ready, focusedId]);

  // Sockets outlive individual components on purpose (that is what makes a
  // UI replacement lossless), so leaving the chat entirely is the one place
  // they must all be closed.
  useEffect(() => () => { evictAll(); }, []);

  return refreshSessions;
}
